/**
 * CMDB-панель в карточке организации: список ассетов по типам с
 * лёгким CRUD. Metadata — произвольный JSON (отображаем как key:value
 * в раскрытом блоке).
 */
import { useEffect, useState } from 'react';
import {
  Loader2, Plus, Pencil, Trash2, Save, X, Server, Database,
  Box, Network, Key as KeyIcon, Globe,
} from 'lucide-react';
import {
  cmdbApi, CMDB_TYPE_LABEL, CMDB_STATUS_LABEL,
  type CmdbAsset, type CmdbAssetType, type CmdbAssetStatus,
} from '../api/cmdb';

const TYPE_KEYS: CmdbAssetType[] = [
  'server', 'vm', 'container', 'software', 'service',
  'database', 'storage', 'network', 'license', 'integration', 'other',
];

const STATUS_KEYS: CmdbAssetStatus[] = [
  'draft', 'active', 'in_maintenance', 'decommissioned',
];

const STATUS_COLOR: Record<CmdbAssetStatus, string> = {
  draft: 'bg-slate-700 text-slate-300',
  active: 'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  in_maintenance: 'bg-yellow-900/40 text-yellow-300 border-yellow-800',
  decommissioned: 'bg-slate-800 text-slate-500',
};

function typeIcon(t: CmdbAssetType) {
  switch (t) {
    case 'server':
    case 'vm':
    case 'container': return <Server size={12} />;
    case 'database': return <Database size={12} />;
    case 'storage': return <Box size={12} />;
    case 'network': return <Network size={12} />;
    case 'license': return <KeyIcon size={12} />;
    case 'integration': return <Globe size={12} />;
    default: return <Box size={12} />;
  }
}

export default function CmdbPanel({ orgId }: { orgId: string }) {
  const [items, setItems] = useState<CmdbAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<CmdbAssetType | 'all'>('all');
  const [editing, setEditing] = useState<CmdbAsset | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await cmdbApi.list(orgId)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [orgId]);

  const filtered = filter === 'all' ? items : items.filter(a => a.type === filter);
  const counts: Record<CmdbAssetType, number> = items.reduce((acc, a) => {
    acc[a.type] = (acc[a.type] || 0) + 1; return acc;
  }, {} as Record<CmdbAssetType, number>);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1 items-center">
        <button onClick={() => setFilter('all')}
          className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase ${
            filter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400'
          }`}>Все ({items.length})</button>
        {TYPE_KEYS.filter(t => counts[t]).map(t => (
          <button key={t} onClick={() => setFilter(t)}
            className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase inline-flex items-center gap-1 ${
              filter === t ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400'
            }`}>
            {typeIcon(t)} {CMDB_TYPE_LABEL[t]} ({counts[t]})
          </button>
        ))}
        <button onClick={() => setEditing('new')}
          className="ml-auto inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-xs font-bold">
          <Plus size={12} /> Asset
        </button>
      </div>

      {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-4" /> :
       filtered.length === 0 ? (
         <div className="text-xs text-slate-500 text-center py-4">
           Активов нет. Добавь первую запись (например, сервер или лицензию).
         </div>
       ) : (
         <div className="space-y-2">
           {filtered.map(a => (
             <CmdbAssetCard key={a.id} a={a}
               onEdit={() => setEditing(a)}
               onDelete={async () => {
                 if (!window.confirm(`Удалить ${a.name}?`)) return;
                 await cmdbApi.delete(orgId, a.id); load();
               }} />
           ))}
         </div>
       )}

      {editing && (
        <CmdbModal orgId={orgId} item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function CmdbAssetCard({ a, onEdit, onDelete }:
  { a: CmdbAsset; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="bg-slate-950 border border-slate-800 rounded p-2.5 text-xs">
      <header className="flex justify-between items-baseline">
        <div>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-300 text-[10px] font-bold uppercase mr-2">
            {typeIcon(a.type)} {CMDB_TYPE_LABEL[a.type]}
          </span>
          <b className="text-slate-200">{a.name}</b>
          <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${STATUS_COLOR[a.status]}`}>
            {CMDB_STATUS_LABEL[a.status]}
          </span>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="text-slate-500 hover:text-blue-400 p-1">
            <Pencil size={11} />
          </button>
          <button onClick={onDelete} className="text-slate-500 hover:text-red-400 p-1">
            <Trash2 size={11} />
          </button>
        </div>
      </header>
      {a.description && (
        <div className="text-[11px] text-slate-400 mt-0.5">{a.description}</div>
      )}
      {a.metadata && Object.keys(a.metadata).length > 0 && (
        <div className="text-[10px] text-slate-500 mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-3">
          {Object.entries(a.metadata).map(([k, v]) => (
            <div key={k}>
              <span className="text-slate-600">{k}:</span> {String(v)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CmdbModal({ orgId, item, onClose, onSaved }:
  { orgId: string; item: CmdbAsset | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<any>(item ? {
    name: item.name, type: item.type, status: item.status,
    description: item.description || '',
    metadata: item.metadata ? JSON.stringify(item.metadata, null, 2) : '',
    installation_id: item.installation_id || '',
    parent_id: item.parent_id || '',
  } : {
    name: '', type: 'server' as CmdbAssetType, status: 'active' as CmdbAssetStatus,
    description: '', metadata: '', installation_id: '', parent_id: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const upd = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      let metadata = null;
      if (form.metadata && form.metadata.trim()) {
        try { metadata = JSON.parse(form.metadata); }
        catch { throw new Error('metadata — невалидный JSON'); }
      }
      const payload: any = {
        name: form.name, type: form.type, status: form.status,
        description: form.description || null,
        metadata,
        installation_id: form.installation_id || null,
        parent_id: form.parent_id || null,
      };
      if (item) await cmdbApi.update(orgId, item.id, payload);
      else await cmdbApi.create(orgId, payload);
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <form onSubmit={submit} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg mx-4"
            onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{item ? 'Редактировать asset' : 'Новый asset'}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </header>
        <div className="p-4 grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Название *</span>
            <input value={form.name} onChange={e => upd('name', e.target.value)} required
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Тип</span>
            <select value={form.type} onChange={e => upd('type', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white">
              {TYPE_KEYS.map(t => <option key={t} value={t}>{CMDB_TYPE_LABEL[t]}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Статус</span>
            <select value={form.status} onChange={e => upd('status', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white">
              {STATUS_KEYS.map(s => <option key={s} value={s}>{CMDB_STATUS_LABEL[s]}</option>)}
            </select>
          </label>
          <label className="block col-span-2">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Описание</span>
            <textarea value={form.description} onChange={e => upd('description', e.target.value)} rows={2}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              metadata (JSON, опционально)
            </span>
            <textarea value={form.metadata} onChange={e => upd('metadata', e.target.value)} rows={5}
              placeholder='{"ip":"10.0.0.1","os":"Astra 1.8","vendor":"Yadro"}'
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white font-mono" />
          </label>
          {err && <div className="col-span-2 bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-800">
          <button type="button" onClick={onClose} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded text-sm">Отмена</button>
          <button type="submit" disabled={busy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded text-sm font-bold inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Сохранить
          </button>
        </div>
      </form>
    </div>
  );
}
